import { IdvProvider } from 'src/features/property';
import { CLEAR_LOGO_PNG, INCODE_LOGO_PNG, SNAPPT_LOGO_SVG } from '../constants/logos';

const escapeHtml = (text: string | undefined): string => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export interface QrCodeTemplateParams {
  propertyName: string;
  companyName?: string;
  qrCodeBase64: string;
  provider?: IdvProvider;
}

export const generateQrCodeHtml = (params: QrCodeTemplateParams): string => {
  const { propertyName, companyName, qrCodeBase64, provider = 'clear' } = params;

  if (!qrCodeBase64.startsWith('data:image/png;base64,')) {
    throw new Error('Invalid qrCodeBase64: must be a PNG data URI');
  }

  // Escape user-provided data to prevent XSS
  const safePropertyName = escapeHtml(propertyName);
  const safeCompanyName = escapeHtml(companyName);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Identity Verification - ${safePropertyName}</title>
  <style>
    :root {
      /* Layout & Spacing */
      --container-max-width: 500px;
      --header-gap: 32px;
      --header-margin-top: 40px;
      --header-margin-bottom: 24px;
      --body-padding: 20px;

      /* Typography - Line Heights */
      --line-height-tight: 1.15;
      --line-height-normal: 1.2;
      --line-height-relaxed: 1.5;

      /* Typography - Opacity */
      --opacity-high: 0.95;
      --opacity-medium: 0.85;
      --opacity-low: 0.7;

      /* Circular QR Pattern */
      --circular-container-size: 464px;
      --circular-container-size-mobile: 320px;
      --qr-image-size: 464px;
      --qr-image-size-mobile: 320px;

      /* Logos & Dividers */
      --logo-height: 48px;
      --logo-height-mobile: 32px;
      --divider-height: 60px;
      --divider-opacity: 0.45;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      margin: 0;
      min-height: 100vh;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: radial-gradient(
        circle at 50% 40%,
        #102b46 0%,
        #0d2338 35%,
        #0a1a2c 60%,
        #081422 100%
      );

      display: flex;
      justify-content: center;
      align-items: center;
      color: #fff;
      padding: var(--body-padding);
    }
    .container {
      width: 100%;
      max-width: var(--container-max-width);
      text-align: center;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--header-gap);
      margin-top: var(--header-margin-top);
      margin-bottom: var(--header-margin-bottom);
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .snappt-logo {
      display: flex;
      align-items: center;
      padding-right: 6px;
    }
    .snappt-logo img {
      height: var(--logo-height);
      width: auto;
      display: block;
    }
    .divider {
      width: 1px;
      height: var(--divider-height);
      background: rgba(255, 255, 255, var(--divider-opacity));
    }
    .company {
      text-align: left;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .company-primary {
      font-size: 1.875rem;
      font-weight: 600;
      line-height: var(--line-height-tight);
      letter-spacing: 0.3px;
      opacity: var(--opacity-high);
    }
    .company-secondary {
      font-size: 1.625rem;
      font-weight: 400;
      opacity: var(--opacity-medium);
      line-height: var(--line-height-tight);
    }
    .protected-text {
      font-size: 1.0625rem;
      opacity: var(--opacity-low);
      margin-top: 12px;
      margin-bottom: 20px;
      letter-spacing: 0.3px;
    }
    .property-name {
      font-size: 2.625rem;
      font-weight: 700;
      margin-bottom: 20px;
      text-align: center;
      line-height: var(--line-height-normal);
    }
    .qr-wrapper {
      display: flex;
      justify-content: center;
      margin-top: 10px;
      margin-bottom: 10px;
      position: relative;
    }
    .circular-container {
      position: relative;
      width: var(--circular-container-size);
      height: var(--circular-container-size);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .qr-image {
      display: block;
      width: var(--qr-image-size);
      height: var(--qr-image-size);
      object-fit: contain;
    }

    .scan-text {
      font-size: 1.75rem;
      margin-top: 32px;
      margin-bottom: 20px;
      opacity: 0.9;
      line-height: var(--line-height-relaxed);
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
    }
    .clear-logo-container {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 12px;
    }
    .pdf-button {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      background-color: rgba(255, 255, 255, 0.1);
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 8px;
      color: white;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      z-index: 1000;
    }
    .pdf-button:hover {
      background-color: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-1px);
    }
    .pdf-button:active {
      transform: translateY(0);
    }
    .pdf-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
    @media (max-width: 550px) {
      .container {
        max-width: 95vw;
      }
      .circular-container {
        width: var(--circular-container-size-mobile);
        height: var(--circular-container-size-mobile);
      }
      .qr-image {
        width: var(--qr-image-size-mobile);
        height: var(--qr-image-size-mobile);
      }
      .property-name {
        font-size: 1.75rem;
      }
      .header {
        gap: 24px;
      }
      .snappt-logo img {
        height: var(--logo-height-mobile);
      }
      .company-primary {
        font-size: 1.125rem;
      }
      .company-secondary {
        font-size: 0.9375rem;
      }
      .scan-text {
        font-size: 0.9375rem;
      }
    }
  </style>
</head>
<body>
  <div class="container" id="qr-content-container">
    <div class="header">
      <div class="brand">
        <div class="snappt-logo">${SNAPPT_LOGO_SVG}</div>
      </div>

      <div class="divider"></div>

      <div class="company">
        <div class="company-primary">${safeCompanyName}</div>
        <div class="company-secondary">Properties</div>
      </div>
    </div>
    <div class="protected-text">
      Application Process Protected by Snappt
    </div>
    <div class="property-name">
      ${safePropertyName}
    </div>
    <div class="qr-wrapper">
      <div class="circular-container">
        <img src="${qrCodeBase64}" alt="Identity Verification QR Code" class="qr-image" />
      </div>
    </div>
    <div class="scan-text">
      Scan here to begin the identity<br />
      verification process with ${provider === 'incode' ? 'incode&reg;' : 'CLEAR&reg;'}
    </div>
    <div class="clear-logo-container">
      ${provider === 'incode' ? INCODE_LOGO_PNG : CLEAR_LOGO_PNG}
    </div>
    <button class="pdf-button" id="pdf-download-btn">Download as PDF</button>
  </div>
  <script>
    function downloadAsPDF() {
      const button = document.getElementById('pdf-download-btn');
      button.textContent = 'Generating PDF...';
      button.disabled = true;

      window.addEventListener('message', function(event) {
        // Validate message origin to prevent processing messages from untrusted sources
        if (event.origin !== window.location.origin) {
          return;
        }

        // Validate message source - must come from the window that opened this popup
        if (event.source !== window.opener) {
          return;
        }

        // Validate message data structure and type
        if (!event.data || typeof event.data !== 'object' || typeof event.data.type !== 'string') {
          return;
        }

        if (event.data.type === 'QR_PDF_COMPLETE' || event.data.type === 'QR_PDF_ERROR') {
          button.textContent = 'Download as PDF';
          button.disabled = false;
        }
      }, { once: true }); // One-time listener - automatically removes after handling response

      if (window.opener && typeof window.opener.postMessage === 'function') {
        window.opener.postMessage({
          type: 'GENERATE_QR_PDF',
          elementId: 'qr-content-container'
        }, window.location.origin);
      } else {
        console.error('identityVerification/qrCodeTemplate/downloadAsPDF', {
          error: 'window.opener is not available',
          operation: 'qr_code_pdf_generation',
          phase: 'popup_communication'
        });
        button.textContent = 'Download as PDF';
        button.disabled = false;
      }
    }

    document.getElementById('pdf-download-btn').addEventListener('click', downloadAsPDF);
  </script>
</body>
</html>
  `;
};
